import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { t } from "i18next";
import { toast } from "react-toastify";
import { getErrorMessageFromErrorResponse } from "shared/welda/helpers/serverResponseParsers";
import clientServices from "webshop/services/clientServices";

import { CART_PROCESS_TYPE, USER_TYPE } from "shared";
import { queryClient } from "webshop/services/queryClient";
import { reset as resetDias } from "../dias/diasSlice";
import { getCart, reset as resetECommerce } from "../eCommerce/eCommerceSlice";
import errorHandling from "../errorHandling";
import eventService from "../event/eventService";
import { reset as resetLabels } from "../labels/labelsSlice";
import { reset as resetMMS } from "../MMS/MMSSlice";
import { reset as resetNotification } from "../notification/notificationSlice";
import { reset as resetPricetags } from "../pricetags/pricetagsSlice";
import { reset as resetSQL } from "../sql/SQLSlice";
import { reset as resetTracking } from "../tracking/trackingSlice";
import employeeService from "./employeeService";
import usersService from "./usersService";

// import rest from all slices

const initialState = {
  user: null, //TODO: Change to employee when feasible.
  client: null,
  hasVisitorManuallySetLanguage: false,
  guestLogin: null,
  error: null,
  isError: false,
  isSuccess: false,
  isLoading: false, // Start as false since we haven't attempted auth yet
  isLoggingIn: false,
  hasAttemptedAuth: false, // Track if we've attempted authentication
  message: "",
};

export const loginEmployee = createAsyncThunk(
  "users/employee/login",
  async (formData, thunkAPI) => {
    try {
      const data = await employeeService.loginEmployee(formData);
      const { employee, isNewEmployee } = data;
      if (isNewEmployee) {
        toast.info(`Gebruiker aangemaakt. Dag ${employee.name}!`, {
          theme: "colored",
        });
      } else {
        toast.success(`Dag ${employee.name}`, {
          theme: "colored",
        });
      }
      const user = employee;
      const payload = { user };
      return payload;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error, t);
      const isBlocked =
        error.response?.status === 403 ||
        error.response?.data?.i18nKey ===
          "backend.user.login.error.user_blocked";

      if (!isBlocked) {
        errorHandling(error);
      }

      const payload = { message, isBlocked };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

export const loginClient = createAsyncThunk(
  "users/client/login",
  async (formData, thunkAPI) => {
    try {
      const data = await clientServices.loginClient(formData);
      const client = data.client;
      localStorage.setItem("i18nextLng", client?.language || "nl");
      thunkAPI.dispatch(getCart());

      toast.success(`${t("common.greet.text")} ${client.name}!`, {
        theme: "colored",
      });

      let cartProcessMessageKey = null;
      switch (data.cartProcessType) {
        case CART_PROCESS_TYPE.NOTHING:
          break;
        case CART_PROCESS_TYPE.TRANSFER:
          cartProcessMessageKey = "frontend.intranet.users.cartProcess.transfer.text";
          break;
        case CART_PROCESS_TYPE.RECOVER:
          cartProcessMessageKey = "frontend.intranet.users.cartProcess.recover.text";
          break;
        case CART_PROCESS_TYPE.MERGE:
          cartProcessMessageKey = "frontend.intranet.users.cartProcess.merge.text";
          break;
      }

      if (cartProcessMessageKey) {
        toast.info(t(cartProcessMessageKey), {
          theme: "colored",
        });
      }

      const payload = { client };
      return payload;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error, t);
      const blockedI18nKey = error.response?.data?.i18nKey;
      const isBlocked =
        blockedI18nKey ===
          "backend.user.client.login.error.account_disabled" ||
        blockedI18nKey ===
          "backend.user.client.login.error.account_blocked";

      const payload = { message, isBlocked, blockedI18nKey };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

export const loginAsUser = createAsyncThunk(
  "users/loginAsUser",
  async (userid, thunkAPI) => {
    try {
      const { user } = thunkAPI.getState().users;

      const data = await employeeService.loginAsUser(userid);
      const loginAsUser = data.loginAsUser;
      // NOTE: The new users' superuser either the localUser's superuser or the localUser itself
      loginAsUser.superuser = user.superuser ? user.superuser : user;
      const payload = {
        user: {
          ...loginAsUser,
          role:
            loginAsUser.roles?.find((role) => role.isDefault)?.key ||
            loginAsUser.roles?.[0]?.key ||
            null,
        },
      };

      // Reset ecommerce state after login as user
      thunkAPI.dispatch(resetECommerce());

      // Invalidate all React Query queries to force refetch
      queryClient.invalidateQueries();

      reloadPage(userid);

      return payload;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error);
      const payload = { message };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

const reloadPage = (userid) => {
  // Force route reload without browser refresh by updating URL with timestamp
  // This will cause React Router to remount components and trigger useEffect hooks
  if (typeof window !== "undefined") {
    // Dispatch custom event that components can listen to
    window.dispatchEvent(
      new CustomEvent("userImpersonated", {
        detail: { userId: userid },
      })
    );

    // Update URL with timestamp to force React Router remount
    // This preserves impersonation state while triggering component remounts
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const searchParams = new URLSearchParams(currentSearch);
    searchParams.set("_reload", Date.now().toString());
    const newUrl = `${currentPath}?${searchParams.toString()}`;

    // Use replaceState to update URL without triggering navigation
    window.history.replaceState({}, "", newUrl);

    // Dispatch popstate event to notify React Router
    window.dispatchEvent(new PopStateEvent("popstate", { state: {} }));
  }
};

export const resetImpersonate = createAsyncThunk(
  "users/resetImpersonate",
  async (_, thunkAPI) => {
    try {
      await employeeService.resetImpersonate();
      reloadPage(null);
      return;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error);
      const payload = { message };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

export const logoutEmployee = createAsyncThunk(
  "users/logoutEmployee",
  async () => {
    // Mark this as a normal logout
    sessionStorage.setItem("normal-logout", "true");

    await employeeService.logoutEmployee();
    const payload = { user: null };
    toast.success(t("common.logged-out.text"), {
      theme: "colored",
    });

    return payload;
  }
);

export const logoutClient = createAsyncThunk(
  "users/logoutClient",
  async (_, thunkAPI) => {
    // Mark this as a normal logout
    sessionStorage.setItem("normal-logout", "true");
    await clientServices.logoutClient();
    localStorage.removeItem("i18nextLng");
    localStorage.removeItem("isDark");
    queryClient.clear();
    // thunkAPI.dispatch(unmountCart());
    thunkAPI.dispatch(resetECommerce());
    thunkAPI.dispatch(resetDias());
    thunkAPI.dispatch(resetMMS());
    thunkAPI.dispatch(resetPricetags());
    thunkAPI.dispatch(resetLabels());
    thunkAPI.dispatch(resetNotification());
    thunkAPI.dispatch(resetSQL());
    thunkAPI.dispatch(resetTracking());
    const payload = { client: null };
    toast.success(t("common.logged-out.text"), {
      theme: "colored",
    });
    return payload;
  }
);

export const getMe = createAsyncThunk(
  "users/get-me",
  async (params, thunkAPI) => {
    try {
      const data = await usersService.getMe();
      const user = data.user;
      localStorage.setItem("i18nextLng", user?.language || "nl");
      let payload = {};
      if (user.userType === USER_TYPE.EMPLOYEE) {
        payload = { user };
      } else if (user.userType === USER_TYPE.CLIENT) {
        payload = { client: user };
      } else {
        payload = { user: null };
      }
      return payload;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error);
      const payload = { message };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

export const loadGuestLogin = createAsyncThunk(
  "users/loadGuestLogin",
  async (input, thunkAPI) => {
    try {
      const data = await eventService.loadGuestLogin(input);
      const payload = { guestLogin: data.guestLogin };
      return payload;
    } catch (error) {
      console.error("error", error);
      const message = getErrorMessageFromErrorResponse(error);
      toast.error(message, {
        theme: "colored",
      });
      console.error("message", message);
      const payload = { message };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

const setPending = (state) => {
  state.hasAttemptedAuth = true; // Mark that we've attempted authentication
  state.isLoading = true;
  state.isError = false;
};

const setLogoutPending = (state) => {
  state.isError = false;
};

const setLoginPending = (state) => {
  state.isLoggingIn = true;
  state.isError = false;
};


const applyAuthFulfilled = (state, action) => {
  state.isLoading = false;
  state.isSuccess = true;
  state.error = null;
  state.message = action?.payload?.message || "";
  const user = action?.payload?.user || null;
  if (user) {
    const roleKey =
      user?.roles?.find((role) => role.isDefault)?.key ||
      user?.roles?.[0]?.key ||
      null;
    state.user = { ...user, role: roleKey };
  } else {
    state.user = null;
  }
  state.client = action?.payload?.client || null;
};

const applyAuthRejected = (state, action) => {
  state.isLoading = false;
  state.isError = true;
  const error = action.payload?.error;
  if (error) {
    state.error = {
      message: error.message || error.toString(),
      code: error.code,
      status: error.response?.status || error.status,
      statusText: error.response?.statusText || error.statusText,
    };
  } else {
    state.error = null;
  }
  state.message = action.payload?.message || "";
};

const setFulfilled = (state, action) => {
  applyAuthFulfilled(state, action);
};

const setRejected = (state, action) => {
  applyAuthRejected(state, action);
};

const setLoginFulfilled = (state, action) => {
  applyAuthFulfilled(state, action);
  state.hasAttemptedAuth = true;
  state.isLoggingIn = false;
};

const setLoginRejected = (state, action) => {
  applyAuthRejected(state, action);
  state.isLoggingIn = false;
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      state.user = null;
      state.client = null;
      state.hasAttemptedAuth = false; // Reset auth attempt flag
    },

    completeAuthCheck: (state) => {
      state.hasAttemptedAuth = true;
      state.isLoading = false;
    },

    setHasVisitorManuallySetLanguage: (state, action) => {
      state.hasVisitorManuallySetLanguage = action.payload;
    },

    setRole: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, role: action.payload };
      }
    },
    resetRole: (state) => {
      if (state.user) {
        state.user = { ...state.user, role: null };
      }
    },

    // resetImpersonate: (state) => {
    //   state.user = state.user.superuser;
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGuestLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadGuestLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.guestLogin = action.payload.guestLogin;
      })
      .addCase(loadGuestLogin.rejected, (state, action) => {
        state.isLoading = false;
        // Extract only serializable error data to avoid Redux warnings
        const error = action.payload?.error;
        if (error) {
          state.error = {
            message: error.message || error.toString(),
            code: error.code,
            status: error.response?.status || error.status,
            statusText: error.response?.statusText || error.statusText,
          };
        } else {
          state.error = null;
        }
        state.message = action.payload?.message || "";
      })
      .addCase(loginEmployee.pending, setLoginPending)
      .addCase(loginEmployee.fulfilled, setLoginFulfilled)
      .addCase(loginEmployee.rejected, setLoginRejected)
      .addCase(loginClient.pending, setLoginPending)
      .addCase(loginClient.fulfilled, setLoginFulfilled)
      .addCase(loginClient.rejected, setLoginRejected)
      .addCase(loginAsUser.pending, setPending)
      .addCase(loginAsUser.fulfilled, setFulfilled)
      .addCase(loginAsUser.rejected, setRejected)
      .addCase(resetImpersonate.pending, setPending)
      .addCase(resetImpersonate.fulfilled, (state) => {
        if (state.user?.superuser) {
          const superuser = state.user.superuser;
          const roleKey =
            superuser?.roles?.find((role) => role.isDefault)?.key ||
            superuser?.roles?.[0]?.key ||
            null;
          state.user = { ...superuser, role: roleKey };
        }
      })
      .addCase(resetImpersonate.rejected, setRejected)
      .addCase(logoutEmployee.pending, setLogoutPending)
      .addCase(logoutEmployee.fulfilled, setFulfilled)
      .addCase(logoutEmployee.rejected, setRejected)
      .addCase(logoutClient.pending, setLogoutPending)
      .addCase(logoutClient.fulfilled, setFulfilled)
      .addCase(logoutClient.rejected, setRejected)
      .addCase(getMe.pending, setPending)
      .addCase(getMe.fulfilled, setFulfilled)
      .addCase(getMe.rejected, setRejected);
  },
});

export const {
  reset,
  completeAuthCheck,
  setHasVisitorManuallySetLanguage,
  setRole,
  resetRole,
} = usersSlice.actions;
export default usersSlice.reducer;
